// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The parts tray (M13-06, 06 §9).
//
// Build mode's palette is not a menu and not a ray target. It is a shelf hovering over the
// lockdown bar: six category tiles in a shallow arc about a quarter of a metre above the bar,
// and under them the open category's parts on holographic plinths, at **true size**, close
// enough that the author's own hand reaches every one of them without leaning. Everything on it
// is inside `reachRange` by construction, because ADR 0015's whole bet is that a part is picked
// up and put on the machine, and a thing you cannot reach cannot be picked up.
//
// The tray's own frame is the machine's near edge: origin at the centre of the lockdown bar,
// +x to the author's right, +y up, +z toward the author (so the machine is at −z). The caller
// places the group at the bar with the machine's yaw and never scales it — a tray scales with
// the author's arm, not with the cabinet, which is the `PLAYER_GAP` lesson from `viewerRay.ts`
// applied to a shelf.
//
// Tiles are tapped (`tap.ts`). Parts are pinched off their plinths, which is `partCarry.ts`'s
// business (M13-07); this file answers "which plinth is that fingertip at" and nothing more.
//
// The house recipe for a panel is `wristPanel.ts`'s and is followed here rather than
// re-derived: paint a canvas, one `CanvasTexture`, one plane, `document` guarded so the tray
// builds headless in a test, and the paint diffed on the words it would draw.

import {type EditorPart as MachineDevice,type PartKind as DeviceKind} from '../../builder/parts'
import {
  Box3,
  CanvasTexture,
  CircleGeometry,
  DoubleSide,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  RingGeometry,
  SRGBColorSpace,
  Vector3,
  type BufferGeometry,
  type Material,
  type Object3D,
  type Texture,
} from 'three'
import type {  } from '@core/machine'
import { CATEGORIES, makeDevice, partLabel, refusalFor, trayPartsOf, type FlipperHand, type PaletteCategory } from '../../builder/palette'
import type { PlayfieldSize } from '../../builder/viewport'
import { buildDeviceMesh } from '../../world/deviceMeshes'
import { withSlingAssembly } from '../../world/slingAssembly'
import { FINISHES, PartPalette } from '../../world/partsKit'
import { TapGate, type TapState } from './tap'
import { BENCH_EN, type BenchKey, type BenchStrings } from './benchStrings'

/** A category on the tray: a tile, and the kinds it opens onto. The palette's own (M13-12). */
export type TrayCategory = PaletteCategory

/**
 * The six categories, partitioning the palette's sixteen kinds.
 *
 * The palette's table (M13-12): one partition, read by the tray, the 2D Builder's palette and —
 * held by a test that reads the C# — the native `PartsTrayLayout.Categories`.
 */
export const TRAY_CATEGORIES: readonly TrayCategory[] = CATEGORIES

/**
 * A tile: 90 × 34 mm. The height is what a fingertip has to hit, and 14 mm is the documented
 * miss width (`wristPanel.ts`, M8-30), so the tile is well over twice that.
 */
export const TILE = { width: 0.09, height: 0.034 } as const
/** The smallest target Build mode ever offers a fingertip. */
export const MIN_TARGET = 0.016

/**
 * Where the tiles hang: an arc of radius 0.24 m about the bar, 0.26 m up, one tile every 0.40 rad.
 *
 * From a head 0.45 m in front of the bar (`PLAYER_GAP`) and about 0.4 m above it, the farthest
 * tile is ~0.70 m away — inside `ARM_REACH` (0.85 m) with room for a shorter arm. The test
 * holds that number rather than this comment.
 */
export const TILE_ARC = { radius: 0.24, height: 0.26, step: 0.4 } as const

/**
 * Where the plinths sit: a tighter arc, lower, so a part is picked up over the near playfield.
 *
 * **0.64 rad apart, from 0.40** (the owner on build 98, 26 Sep 2026: "component selector,
 * components are too close to each other"). At 0.40 the chord between neighbours was 83 mm against
 * a 100 mm disc, so the discs overlapped and the two flippers' bats crossed. At 0.64 the chord is
 * 2 · 0.21 · sin 0.32 ≈ 132 mm: a 32 mm gap between discs, and a flipper's bat (centred on its
 * plinth, `plinthPartAt`) stays on its own disc.
 */
export const PLINTH_ARC = { radius: 0.21, height: 0.12, step: 0.64 } as const
/**
 * A plinth's name plate (the 1.0.113 round, 27 Sep 2026: "the component categories have titles in
 * build mode but the components themselves do not have names or titles"): 110 × 27.5 mm at the
 * disc's near rim, tipped 40° toward the author, the part's name from the palette. 110 mm leaves a
 * 22 mm gap to the next plate on the 132 mm plinth chord.
 */
export const PLINTH_NAME = { width: 0.11, height: 0.0275, up: 0.014, out: 0.008, tilt: (40 * Math.PI) / 180 } as const
/** The name plate's canvas: four to one, as the plate is. */
const NAME_CANVAS = { width: 512, height: 128 } as const

/**
 * **The remove tray** (the owner, 24 Oct 2026: "a remove tray on the side of the pinball machine
 * that the user can drop a component into"): an open box hung off the cabinet's right side at the
 * front corner, just below the lockdown bar. A placed part carried into it and let go comes off the
 * machine (`PartCarry`); it is the only place on the bench that removes. Room metres, the tray's
 * frame (origin the bar's centre, +x the author's right, +y up, +z toward the author), never scaled:
 * a box is the size of the hand that drops into it, whatever the bench's scale.
 *
 * - `width` across (x), `length` along the cabinet (z), `depth` from the rim down;
 * - `gap` between the cabinet's side and the box; `rimBelow` how far the rim hangs under the bar;
 * - `along` the box's centre behind the bar's line (−z is toward the machine's far end).
 *
 * Beside the cabinet is clear of the board by construction — the cabinet is wider than its
 * playfield — so "anywhere a part can be let go over the board, it is placed" never meets the box.
 * `PartsTrayLayout.Bin*` on the headset.
 */
export const BIN = { width: 0.1, length: 0.14, depth: 0.1, gap: 0.03, rimBelow: 0.03, along: -0.05 } as const

/** How far round the box a hand still counts as in it, room metres, and how far over its rim — a part is dropped in from above. */
export const BIN_SLACK = 0.02
export const BIN_ABOVE = 0.06

/** The box's centre in the tray's frame, for a cabinet `halfWidth` room metres either side of the bar's centre. */
/**
 * Where a part let go over the remove tray comes to rest (24 Oct 2026, the owner on the native
 * 1.0.211: "when user lets go of component it disappears instead of showing being dropped into
 * remove box"): the box's floor under the point, kept `margin` inside its walls. World in, world out.
 */
export function binRest(bin: Object3D, world: Vector3, margin: number): Vector3 {
  const local = bin.worldToLocal(world.clone())
  const w = Math.max(0, BIN.width / 2 - margin)
  const l = Math.max(0, BIN.length / 2 - margin)
  local.x = Math.min(w, Math.max(-w, local.x))
  local.z = Math.min(l, Math.max(-l, local.z))
  local.y = -BIN.depth / 2
  return bin.localToWorld(local)
}

export function binCentre(halfWidth: number): { readonly x: number; readonly y: number; readonly z: number } {
  return { x: halfWidth + BIN.gap + BIN.width / 2, y: -BIN.rimBelow - BIN.depth / 2, z: BIN.along }
}

/** Is a point, relative to the box's centre in the tray's frame, in the box — or just over its rim? */
export function inBin(dx: number, dy: number, dz: number): boolean {
  return (
    Math.abs(dx) <= BIN.width / 2 + BIN_SLACK &&
    Math.abs(dz) <= BIN.length / 2 + BIN_SLACK &&
    dy >= -BIN.depth / 2 - BIN_SLACK &&
    dy <= BIN.depth / 2 + BIN_ABOVE
  )
}

/** A plinth's disc, and how close a fingertip has to be to the part on it to be "at" it. */
export const PLINTH_RADIUS = 0.05
export const PLINTH_REACH = 0.05

/** A pose on one of the tray's arcs, in the tray's frame. Yaw turns the object to face the bar. */
export interface ArcPose {
  readonly x: number
  readonly y: number
  readonly z: number
  readonly yaw: number
}

/** A flipper's bat from pivot to tip, metres (the palette's 76 mm bat). */
const FLIPPER_BAT = 0.076

/**
 * Where a part stands on its plinth, playfield metres from the disc's centre. Every part stands at
 * the centre except a flipper, whose *pivot* is where the palette puts it: the bat reached 66 mm
 * off its disc toward its neighbour, and the left and right bats met in the middle. The pivot goes
 * half a bat back along the rest angle, so the bat's middle is over the disc's centre.
 */
export function plinthPartAt(kind: DeviceKind, hand: FlipperHand | undefined): [number, number] {
  if (kind !== 'flipper') return [0, 0]
  const rest = hand === 'right' ? 3.6652 : -0.5236
  const half = FLIPPER_BAT / 2
  return [Math.round(-half * Math.cos(rest) * 1e6) / 1e6, Math.round(-half * Math.sin(rest) * 1e6) / 1e6]
}

/** Tile `index` of `count`, centred on the arc. Pure, so the layout is testable without Three. */
export function tilePose(index: number, count: number): ArcPose {
  const a = (index - (count - 1) / 2) * TILE_ARC.step
  return { x: Math.sin(a) * TILE_ARC.radius, y: TILE_ARC.height, z: -Math.cos(a) * TILE_ARC.radius, yaw: -a }
}

/**
 * The tray's two history buttons (the 1.0.115 round, 27 Sep 2026: "user should have option to
 * undo and/or redo change as they are building"): Undo one tile-step past the author's left end of
 * the tile arc, Redo one past the right, tile-sized, always up while building. Each says what it
 * would change. `PartsTrayLayout.ActionPose` on the headset.
 */
export type TrayAction = 'undo' | 'redo'
export const TRAY_ACTIONS: readonly TrayAction[] = ['undo', 'redo']

export function actionPose(action: TrayAction, count: number): ArcPose {
  return tilePose(action === 'undo' ? -1 : count, count)
}

/** Plinth `index` of `count`, centred on the arc and scrolled by `offset` radians. */
export function plinthPose(index: number, count: number, offset = 0): ArcPose {
  const a = (index - (count - 1) / 2) * PLINTH_ARC.step + offset
  return { x: Math.sin(a) * PLINTH_ARC.radius, y: PLINTH_ARC.height, z: -Math.cos(a) * PLINTH_ARC.radius, yaw: -a }
}

/** A hand's fingertip in world space, for the tray's hit tests. */
export interface TrayHand {
  readonly handedness: 'left' | 'right'
  readonly present: boolean
  readonly x: number
  readonly y: number
  readonly z: number
}

export interface TrayTile {
  readonly index: number
  readonly category: TrayCategory
  readonly mesh: Mesh
}

export interface TrayPlinth {
  readonly index: number
  readonly kind: DeviceKind
  /** A flipper plinth's side (26 Sep 2026): the left flipper and the right flipper are two plinths. */
  readonly hand: FlipperHand | undefined
  readonly group: Group
  /** The prototype the palette would place: what a pinch picks up. */
  readonly device: MachineDevice
  /** Why it cannot be taken from this machine, when it cannot — `refusalFor`'s own sentence. */
  readonly refusal: string | undefined
}

export interface TrayFrame {
  /** A tile pressed this frame, by index. At most one — the first hand to press wins. */
  readonly tapped: number | undefined
  /** The plinth a fingertip is at, per hand, for the caller's hover and pinch. */
  readonly at: Readonly<Record<'left' | 'right', TrayPlinth | undefined>>
  /**
   * Whether each hand is in the remove tray (`inBin`), so a placed part let go there comes off the
   * machine (`PartCarry`'s `removesAt`). Nothing else on the tray removes.
   */
  readonly atTray: Readonly<Record<'left' | 'right', boolean>>
  /** A history button pressed this frame (the 1.0.115 round), at most one. Pressed while it can act only. */
  readonly action?: TrayAction
}

export interface PartsTrayBuild {
  readonly group: Group
  readonly tiles: readonly TrayTile[]
  /** The open category's plinths, in order. Empty until `open`. */
  readonly plinths: readonly TrayPlinth[]
  readonly opened: number | undefined
  /** Open a category: its parts appear on the plinths. */
  open(index: number, playfield: PlayfieldSize, existing: readonly MachineDevice[]): void
  /** Scroll the plinths along their arc, radians; clamped so the first and last stay reachable. */
  scroll(offset: number): void
  /** One frame of fingertips: taps on tiles, and which plinth each hand is at. */
  advance(hands: readonly TrayHand[]): TrayFrame
  /** Hide a plinth's part while it is being carried, and show it again. */
  lift(index: number, lifted: boolean): void
  /**
   * Put the plinths away while a part's card is up, and bring them back (the owner on build 98,
   * 26 Sep 2026: "customization menu is behind the actual components"). The card for a part near
   * the bar stands where the plinth arc is, so the full-size parts on the plinths were in front of
   * it. Stowed plinths are hidden and out of the pinch test; the tiles still take taps.
   */
  stow(stowed: boolean): void
  readonly stowed: boolean
  /** The bench changed language (M13-26): the tiles repaint on their next frame, the next refusal is in the new one. */
  reword(strings: BenchStrings): void
  /**
   * What the history buttons say (the 1.0.115 round): what the next Undo and Redo would change,
   * or undefined for nothing to undo or redo — the button is dimmed and a press does nothing.
   */
  history(undo: string | undefined, redo: string | undefined): void
  /** The Undo and Redo buttons, in `TRAY_ACTIONS` order. */
  readonly actions: readonly Mesh[]
  /** The remove tray: its box and label, lit while a hand is in it. */
  readonly bin: Group
  /** Hang the remove tray off a cabinet `halfWidth` room metres either side of the bar's centre. */
  placeBin(halfWidth: number): void
  dispose(): void
}

const CANVAS = { width: 512, height: 192 } as const

/**
 * What a plinth's picture depends on: the prototype's text with its own id taken out, so
 * `flipper1` and `flipper2` draw alike and a re-read keeps the drawn part (the 1.0.113 round).
 * `PartsTray.KeyOf` on the headset.
 */
export function trayPartKey(device: MachineDevice): string {
  const text = JSON.stringify(device)
  const id = device.id
  return id === '' ? text : text.split(`"${id}`).join('"\u0001').split(`${id}"`).join('\u0001"')
}

/** Build the tray. `document` is optional, so it builds headless. */
/** A tile's label in the bench's language (M13-26), or the palette's English for a category the table has not met. */
function tileLabel(category: PaletteCategory, strings: BenchStrings): string {
  const known: string | undefined = (strings as Readonly<Record<string, string>>)[`tray.${category.id}` as BenchKey]
  return known ?? category.label
}

export function buildPartsTray(strings: BenchStrings = BENCH_EN): PartsTrayBuild {
  // The words the tiles and the refusal are in; `reword` changes them under a tray that is up.
  let words = strings
  const group = new Group()
  group.name = 'partsTray'
  const geometries: BufferGeometry[] = []
  const materials: Material[] = []
  const textures: Texture[] = []
  const palette = new PartPalette()

  const host = (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } })
    .document

  // --- tiles ----------------------------------------------------------------
  const tileGeometry = new PlaneGeometry(TILE.width, TILE.height)
  geometries.push(tileGeometry)
  const tiles: TrayTile[] = []
  const tilePainters: { paint: (active: boolean, pressed: boolean) => void }[] = []
  const tileGates = new Map<number, Record<'left' | 'right', TapGate>>()

  TRAY_CATEGORIES.forEach((category, index) => {
    const canvas = host?.createElement('canvas')
    const ctx = canvas?.getContext('2d') ?? null
    if (canvas !== undefined) {
      canvas.width = CANVAS.width
      canvas.height = CANVAS.height
    }
    const texture = canvas === undefined ? undefined : new CanvasTexture(canvas)
    if (texture !== undefined) {
      texture.colorSpace = SRGBColorSpace
      texture.generateMipmaps = false
      texture.minFilter = LinearFilter
      textures.push(texture)
    }
    const material = new MeshBasicMaterial({
      ...(texture === undefined ? { color: 0x101018 } : { map: texture }),
      transparent: true,
      side: DoubleSide,
      toneMapped: false,
      depthTest: false,
    })
    materials.push(material)
    const mesh = new Mesh(tileGeometry, material)
    mesh.name = `tray.tile.${category.id}`
    // Drawn over the machine it hovers above, as the wrist panel is: a tile occluded by a backbox
    // is a tile that appears not to be there.
    mesh.renderOrder = 998
    const pose = tilePose(index, TRAY_CATEGORIES.length)
    mesh.position.set(pose.x, pose.y, pose.z)
    // Faces the bar's centre, and tips a little toward a head above it.
    mesh.rotation.set(-0.35, pose.yaw, 0, 'YXZ')
    group.add(mesh)
    tiles.push({ index, category, mesh })
    tileGates.set(index, { left: new TapGate(TILE), right: new TapGate(TILE) })

    let lastKey = ''
    tilePainters.push({
      paint: (active, pressed) => {
        if (ctx === null || texture === undefined) return
        const key = `${tileLabel(category, words)}|${active}|${pressed}`
        if (key === lastKey) return
        lastKey = key
        paintTile(ctx, tileLabel(category, words), active, pressed)
        texture.needsUpdate = true
      },
    })
    tilePainters[index]?.paint(false, false)
  })

  // --- the history buttons (the 1.0.115 round) --------------------------------
  let historyWhat: Record<TrayAction, string | undefined> = { undo: undefined, redo: undefined }
  const actionMeshes: Mesh[] = []
  const actionGates = new Map<TrayAction, Record<'left' | 'right', TapGate>>()
  const actionPainters = new Map<TrayAction, (pressed: boolean) => void>()
  for (const action of TRAY_ACTIONS) {
    const canvas = host?.createElement('canvas')
    const ctx = canvas?.getContext('2d') ?? null
    if (canvas !== undefined) {
      canvas.width = CANVAS.width
      canvas.height = CANVAS.height
    }
    const texture = canvas === undefined ? undefined : new CanvasTexture(canvas)
    if (texture !== undefined) {
      texture.colorSpace = SRGBColorSpace
      texture.generateMipmaps = false
      texture.minFilter = LinearFilter
      textures.push(texture)
    }
    const material = new MeshBasicMaterial({
      ...(texture === undefined ? { color: 0x101018 } : { map: texture }),
      transparent: true,
      side: DoubleSide,
      toneMapped: false,
      depthTest: false,
    })
    materials.push(material)
    const mesh = new Mesh(tileGeometry, material)
    mesh.name = `tray.action.${action}`
    mesh.renderOrder = 998
    const pose = actionPose(action, TRAY_CATEGORIES.length)
    mesh.position.set(pose.x, pose.y, pose.z)
    mesh.rotation.set(-0.35, pose.yaw, 0, 'YXZ')
    group.add(mesh)
    actionMeshes.push(mesh)
    actionGates.set(action, { left: new TapGate(TILE), right: new TapGate(TILE) })
    let lastKey = ''
    const paint = (pressed: boolean): void => {
      const what = historyWhat[action]
      mesh.userData['what'] = what
      if (ctx === null || texture === undefined) return
      const title = action === 'undo' ? words['menu.undo'] : words['menu.redo']
      const idle = action === 'undo' ? words['menu.nothingToUndo'] : words['menu.nothingToRedo']
      const key = `${title}|${what ?? ''}|${idle}|${pressed}`
      if (key === lastKey) return
      lastKey = key
      paintAction(ctx, what === undefined ? idle : title, what, what !== undefined, pressed)
      texture.needsUpdate = true
    }
    actionPainters.set(action, paint)
    paint(false)
  }

  // --- the remove tray (the owner, 24 Oct 2026) -------------------------------
  // An open box — a floor and four walls in the hologram's see-through glass, red-tinted so it reads
  // as the place things go *out* — and a label over its near wall, painted as the history buttons are.
  const bin = new Group()
  bin.name = 'tray.bin'
  const binGlass = new MeshBasicMaterial({ color: 0xff5a5a, transparent: true, opacity: 0.22, side: DoubleSide, toneMapped: false, depthWrite: false })
  const binRim = new MeshBasicMaterial({ color: 0xff7a7a, transparent: true, opacity: 0.6, side: DoubleSide, toneMapped: false, depthWrite: false })
  materials.push(binGlass, binRim)
  const wall = (w: number, h: number, x: number, y: number, z: number, rx: number, ry: number, name: string): void => {
    const g = new PlaneGeometry(w, h)
    geometries.push(g)
    const m = new Mesh(g, binGlass)
    m.name = name
    m.position.set(x, y, z)
    m.rotation.set(rx, ry, 0)
    bin.add(m)
  }
  wall(BIN.width, BIN.length, 0, -BIN.depth / 2, 0, -Math.PI / 2, 0, 'tray.bin.floor')
  wall(BIN.width, BIN.depth, 0, 0, BIN.length / 2, 0, 0, 'tray.bin.near')
  wall(BIN.width, BIN.depth, 0, 0, -BIN.length / 2, 0, 0, 'tray.bin.far')
  wall(BIN.length, BIN.depth, -BIN.width / 2, 0, 0, 0, Math.PI / 2, 'tray.bin.inner')
  wall(BIN.length, BIN.depth, BIN.width / 2, 0, 0, 0, Math.PI / 2, 'tray.bin.outer')
  // The rim, as a thin frame: four bars along the top edges.
  for (const [w, d, x, z] of [
    [BIN.width + 0.006, 0.006, 0, BIN.length / 2],
    [BIN.width + 0.006, 0.006, 0, -BIN.length / 2],
    [0.006, BIN.length, BIN.width / 2, 0],
    [0.006, BIN.length, -BIN.width / 2, 0],
  ] as const) {
    const g = new PlaneGeometry(w, d)
    geometries.push(g)
    const bar = new Mesh(g, binRim)
    bar.name = 'tray.bin.rim'
    bar.position.set(x, BIN.depth / 2, z)
    bar.rotation.set(-Math.PI / 2, 0, 0)
    bin.add(bar)
  }
  const binCanvas = host?.createElement('canvas')
  const binCtx = binCanvas?.getContext('2d') ?? null
  if (binCanvas !== undefined) {
    binCanvas.width = CANVAS.width
    binCanvas.height = CANVAS.height
  }
  const binTexture = binCanvas === undefined ? undefined : new CanvasTexture(binCanvas)
  if (binTexture !== undefined) {
    binTexture.colorSpace = SRGBColorSpace
    binTexture.generateMipmaps = false
    binTexture.minFilter = LinearFilter
    textures.push(binTexture)
  }
  const labelMaterial = new MeshBasicMaterial({
    ...(binTexture === undefined ? { color: 0x101018 } : { map: binTexture }),
    transparent: true,
    side: DoubleSide,
    toneMapped: false,
    depthTest: false,
  })
  materials.push(labelMaterial)
  const label = new Mesh(tileGeometry, labelMaterial)
  label.name = 'tray.bin.label'
  label.renderOrder = 998
  // Standing on the near rim, tipped toward the author as the tiles are.
  label.position.set(0, BIN.depth / 2 + TILE.height / 2 + 0.008, BIN.length / 2)
  label.rotation.set(-0.35, 0, 0, 'YXZ')
  bin.add(label)
  group.add(bin)
  const placeBin = (halfWidth: number): void => {
    const c = binCentre(Math.abs(halfWidth))
    bin.position.set(c.x, c.y, c.z)
  }
  placeBin(0.15)
  let binKey = ''
  const paintBin = (lit: boolean): void => {
    bin.userData['lit'] = lit
    binGlass.opacity = lit ? 0.5 : 0.22
    binRim.opacity = lit ? 1 : 0.6
    if (binCtx === null || binTexture === undefined) return
    const key = `${words['tray.bin']}|${words['tray.binHint']}|${lit}`
    if (key === binKey) return
    binKey = key
    paintAction(binCtx, words['tray.bin'], words['tray.binHint'], true, lit)
    binTexture.needsUpdate = true
  }
  paintBin(false)

  // --- plinths --------------------------------------------------------------
  const plinthGeometry = new CircleGeometry(PLINTH_RADIUS, 40)
  const haloGeometry = new RingGeometry(PLINTH_RADIUS - 0.001, PLINTH_RADIUS + 0.004, 40)
  geometries.push(plinthGeometry, haloGeometry)
  // The `hologram` finish (M13-18): the plinth's disc, the same on both trays.
  const plinthMaterial = new MeshBasicMaterial({
    color: FINISHES.hologram.color,
    transparent: true,
    opacity: FINISHES.hologram.opacity ?? 0.25,
    depthWrite: false,
    side: DoubleSide,
  })
  const haloMaterial = new MeshBasicMaterial({ color: 0x9fd8ff, transparent: true, opacity: 0.35, depthWrite: false, side: DoubleSide })
  materials.push(plinthMaterial, haloMaterial)

  let plinths: TrayPlinth[] = []
  // A plinth as drawn, kept per category once built (the 1.0.113 round, 27 Sep 2026: every drop
  // and every card release re-opened the category and re-built every part and name plate — the
  // 360 ms and 500 ms frames). A prototype that differs from the kept one only by its id —
  // `flipper1` taken, so the plinth now offers `flipper2` — keeps its picture.
  interface BuiltPlinth {
    readonly kind: DeviceKind
    readonly hand: FlipperHand | undefined
    readonly key: string
    readonly label: string
    readonly group: Group
    readonly part: Object3D | undefined
    readonly geometries: readonly BufferGeometry[]
    readonly plate: { dispose(): void }
  }
  const kept = new Map<number, BuiltPlinth[]>()
  const destroy = (built: BuiltPlinth): void => {
    group.remove(built.group)
    for (const g of built.geometries) g.dispose()
    built.plate.dispose()
  }
  const nameGeometry = new PlaneGeometry(PLINTH_NAME.width, PLINTH_NAME.height)
  geometries.push(nameGeometry)
  let opened: number | undefined
  let offset = 0
  let stowed = false

  /** The open category's plinths off the tray — kept, not thrown away. */
  const clearPlinths = (): void => {
    for (const p of plinths) group.remove(p.group)
    plinths = []
  }

  const layout = (): void => {
    plinths.forEach((p, j) => {
      const pose = plinthPose(j, plinths.length, offset)
      p.group.position.set(pose.x, pose.y, pose.z)
      p.group.rotation.set(0, pose.yaw, 0)
      p.group.visible = !stowed
    })
  }

  const stow = (next: boolean): void => {
    if (next === stowed) return
    stowed = next
    for (const p of plinths) p.group.visible = !stowed
  }

  const open = (index: number, playfield: PlayfieldSize, existing: readonly MachineDevice[]): void => {
    const category = TRAY_CATEGORIES[index]
    if (category === undefined) return
    const again = opened === index
    clearPlinths()
    opened = index
    // The category already open, re-read against the machine (a drop, a card's release): its
    // scroll stays where the author left it.
    if (!again) offset = 0
    const shelf = kept.get(index) ?? []
    kept.set(index, shelf)
    plinths = trayPartsOf(category).map(({ kind, hand }, j) => {
      const device = makeDevice(kind, plinthPartAt(kind, hand), playfield, existing, hand)
      const key = trayPartKey(device)
      const label = partLabel(kind, hand)
      let built = shelf[j]
      if (built !== undefined && built.kind === kind && built.hand === hand && built.key === key && built.label === label) {
        if (built.part !== undefined) {
          built.part.name = device.id
          built.part.visible = true
        }
      } else {
        if (built !== undefined) destroy(built)
        built = makePlinth(kind, hand, device, key, label)
        shelf[j] = built
      }
      group.add(built.group)
      // The palette's one refusal, in the bench's language: the sentence is the table's, the rule the palette's.
      return { index: j, kind, hand, group: built.group, device, refusal: refusalFor(kind, existing) === undefined ? undefined : words['tray.bankNeedsTarget'] }
    })
    layout()
  }

  /** One plinth drawn: the disc, its halo, the prototype laid flat on it, and its name plate. */
  const makePlinth = (kind: DeviceKind, hand: FlipperHand | undefined, device: MachineDevice, key: string, label: string): BuiltPlinth => {
    const plinth = new Group()
    plinth.name = hand === undefined ? `tray.plinth.${kind}` : `tray.plinth.${kind}.${hand}`
    const disc = new Mesh(plinthGeometry, plinthMaterial)
    disc.rotation.x = -Math.PI / 2
    plinth.add(disc)
    const halo = new Mesh(haloGeometry, haloMaterial)
    halo.rotation.x = -Math.PI / 2
    halo.position.y = 0.002
    plinth.add(halo)

    // The prototype at the playfield's centre, so its own coordinates are near zero and the
    // mesh sits on the plinth; a pinch takes *this* device and `partCarry` moves it.
    const built = withSlingAssembly(buildDeviceMesh(device, palette),device,{machineId:''},palette)
    if (built.group !== undefined) {
      // Playfield +z is up; on the tray, up is +y. Lay the part flat on the disc.
      built.group.rotation.x = -Math.PI / 2
      built.group.position.y = 0.004
      plinth.add(built.group)
      if(device.kind==='magnet'){const base=new Box3().setFromObject(disc),body=new Box3().setFromObject(built.group);built.group.position.y+=base.max.y+.004-body.min.y}
    }
    // The part's name on a plate at the disc's near rim (the 1.0.113 round).
    const plate = namePlate(label)
    plate.mesh.position.set(0, PLINTH_NAME.up, PLINTH_RADIUS + PLINTH_NAME.out)
    plate.mesh.rotation.x = -PLINTH_NAME.tilt
    plinth.add(plate.mesh)
    return { kind, hand, key, label, group: plinth, part: built.group, geometries: built.geometries, plate }
  }

  /** One plinth's name plate: the name, fitted to the plate, pale on the hologram's dark. */
  const namePlate = (label: string): { readonly mesh: Mesh; dispose(): void } => {
    const canvas = host?.createElement('canvas')
    const ctx = canvas?.getContext('2d') ?? null
    if (canvas !== undefined) {
      canvas.width = NAME_CANVAS.width
      canvas.height = NAME_CANVAS.height
    }
    const texture = canvas === undefined ? undefined : new CanvasTexture(canvas)
    if (texture !== undefined) {
      texture.colorSpace = SRGBColorSpace
      texture.generateMipmaps = false
      texture.minFilter = LinearFilter
    }
    if (ctx !== null && texture !== undefined) {
      ctx.clearRect(0, 0, NAME_CANVAS.width, NAME_CANVAS.height)
      ctx.fillStyle = 'rgba(18,26,44,0.85)'
      roundRect(ctx, 4, 4, NAME_CANVAS.width - 8, NAME_CANVAS.height - 8, 30)
      ctx.fill()
      ctx.fillStyle = '#e8eef8'
      ctx.font = '600 60px system-ui, sans-serif'
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'center'
      ctx.fillText(label, NAME_CANVAS.width / 2, NAME_CANVAS.height / 2, NAME_CANVAS.width - 40)
      texture.needsUpdate = true
    }
    const material = new MeshBasicMaterial({
      ...(texture === undefined ? { color: 0x101018 } : { map: texture }),
      transparent: true,
      side: DoubleSide,
      toneMapped: false,
      depthTest: false,
    })
    const mesh = new Mesh(nameGeometry, material)
    mesh.name = 'tray.plinth.name'
    mesh.userData['label'] = label
    mesh.renderOrder = 998
    return {
      mesh,
      dispose: () => {
        material.dispose()
        texture?.dispose()
      },
    }
  }

  const scroll = (next: number): void => {
    const span = ((plinths.length - 1) / 2) * PLINTH_ARC.step
    offset = Math.max(-span, Math.min(span, next))
    layout()
  }

  const lift = (index: number, lifted: boolean): void => {
    const plinth = plinths[index]
    if (plinth === undefined) return
    for (const child of plinth.group.children) {
      if (child.name === plinth.device.id) child.visible = !lifted
    }
  }

  // --- per frame ------------------------------------------------------------
  const local = new Vector3()
  const world = new Vector3()

  const advance = (hands: readonly TrayHand[]): TrayFrame => {
    let tapped: number | undefined
    const at: Record<'left' | 'right', TrayPlinth | undefined> = { left: undefined, right: undefined }
    const atTray: Record<'left' | 'right', boolean> = { left: false, right: false }
    group.updateWorldMatrix(true, true)

    for (const tile of tiles) {
      const gates = tileGates.get(tile.index) as Record<'left' | 'right', TapGate>
      let anyDown = false
      for (const hand of hands) {
        const gate = gates[hand.handedness]
        let state: TapState
        if (!hand.present) {
          state = gate.release()
        } else {
          local.set(hand.x, hand.y, hand.z)
          tile.mesh.worldToLocal(local)
          state = gate.advance({ x: local.x, y: local.y, z: local.z })
        }
        if (state.pressedEdge && tapped === undefined) tapped = tile.index
        if (state.down) anyDown = true
      }
      tilePainters[tile.index]?.paint(tile.index === opened, anyDown)
    }

    // The history buttons: tapped as the tiles are; a press on one with nothing to do is nothing.
    let action: TrayAction | undefined
    TRAY_ACTIONS.forEach((which, i) => {
      const mesh = actionMeshes[i] as Mesh
      const gates = actionGates.get(which) as Record<'left' | 'right', TapGate>
      let anyDown = false
      for (const hand of hands) {
        const gate = gates[hand.handedness]
        let state: TapState
        if (!hand.present) {
          state = gate.release()
        } else {
          local.set(hand.x, hand.y, hand.z)
          mesh.worldToLocal(local)
          state = gate.advance({ x: local.x, y: local.y, z: local.z })
        }
        if (state.pressedEdge && action === undefined && tapped === undefined && historyWhat[which] !== undefined) action = which
        if (state.down) anyDown = true
      }
      actionPainters.get(which)?.(anyDown && historyWhat[which] !== undefined)
    })

    // The remove tray: a hand in the box (or just over its rim) is at the tray, and the box lights.
    let binLit = false
    for (const hand of hands) {
      if (!hand.present || !group.visible) continue
      local.set(hand.x, hand.y, hand.z)
      bin.worldToLocal(local)
      if (!inBin(local.x, local.y, local.z)) continue
      atTray[hand.handedness] = true
      binLit = true
    }
    paintBin(binLit)

    for (const hand of hands) {
      if (!hand.present || stowed) continue
      let best: TrayPlinth | undefined
      let bestDistance = PLINTH_REACH
      for (const plinth of plinths) {
        plinth.group.getWorldPosition(world)
        // The part stands a few centimetres above the disc; measure to the part, not the disc.
        world.y += 0.02
        const d = Math.hypot(hand.x - world.x, hand.y - world.y, hand.z - world.z)
        if (d < bestDistance) {
          bestDistance = d
          best = plinth
        }
      }
      at[hand.handedness] = best
    }

    return action === undefined ? { tapped, at, atTray } : { tapped, at, atTray, action }
  }

  return {
    group,
    tiles,
    get plinths() {
      return plinths
    },
    get opened() {
      return opened
    },
    open,
    scroll,
    advance,
    lift,
    stow,
    get stowed() {
      return stowed
    },
    reword: (next) => {
      words = next
    },
    history: (undo, redo) => {
      if (historyWhat.undo === undo && historyWhat.redo === redo) return
      historyWhat = { undo, redo }
      for (const paint of actionPainters.values()) paint(false)
    },
    actions: actionMeshes,
    bin,
    placeBin,
    dispose: () => {
      clearPlinths()
      for (const shelf of kept.values()) for (const built of shelf) destroy(built)
      kept.clear()
      for (const g of geometries) g.dispose()
      for (const m of materials) m.dispose()
      for (const t of textures) t.dispose()
      palette.dispose()
    },
  }
}

/** One tile's picture: its label, brighter when open, filled while a finger is on it. */
function paintTile(
  ctx: CanvasRenderingContext2D,
  label: string,
  active: boolean,
  pressed: boolean,
): void {
  const { width, height } = CANVAS
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = pressed ? 'rgba(159,216,255,0.35)' : active ? 'rgba(18,26,44,0.95)' : 'rgba(18,26,44,0.8)'
  roundRect(ctx, 6, 6, width - 12, height - 12, 24)
  ctx.fill()
  ctx.lineWidth = active ? 6 : 3
  ctx.strokeStyle = active ? '#f5a623' : '#9fd8ff'
  roundRect(ctx, 6, 6, width - 12, height - 12, 24)
  ctx.stroke()
  ctx.fillStyle = active ? '#f5a623' : '#e8eef8'
  ctx.font = '600 64px system-ui, sans-serif'
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'
  ctx.fillText(label, width / 2, height / 2)
}

/**
 * A history button's picture (the 1.0.115 round): the verb, and under it what it would change — or
 * the one dimmed line saying there is nothing to undo or redo.
 */
function paintAction(ctx: CanvasRenderingContext2D, title: string, what: string | undefined, live: boolean, pressed: boolean): void {
  const { width, height } = CANVAS
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = pressed ? 'rgba(159,216,255,0.35)' : live ? 'rgba(18,26,44,0.9)' : 'rgba(18,26,44,0.55)'
  roundRect(ctx, 6, 6, width - 12, height - 12, 24)
  ctx.fill()
  ctx.lineWidth = 3
  ctx.strokeStyle = live ? '#9fd8ff' : '#5a6478'
  roundRect(ctx, 6, 6, width - 12, height - 12, 24)
  ctx.stroke()
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  if (what === undefined) {
    ctx.fillStyle = '#8a93a6'
    ctx.font = '600 44px system-ui, sans-serif'
    ctx.fillText(title, width / 2, height / 2, width - 40)
    return
  }
  ctx.fillStyle = '#e8eef8'
  ctx.font = '600 60px system-ui, sans-serif'
  ctx.fillText(title, width / 2, height * 0.36, width - 40)
  ctx.fillStyle = '#9fd8ff'
  ctx.font = '500 40px system-ui, sans-serif'
  ctx.fillText(what, width / 2, height * 0.72, width - 40)
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}
